import React, { useEffect } from 'react';
import { useSelector, shallowEqual } from 'react-redux';
import classNames from 'classnames';
import { hooks, serviceClient, view } from '@yola/ws-sdk';
import { ViewportConsumer } from 'yola-editor/src/js/utils/viewport-provider';
import ViewOverlayContainer from 'src/js/modules/view-overlay/containers/view-overlay-container';
import TopbarContainer from 'src/js/modules/topbar/containers/topbar-container';
import EmptyPageTriggerContainer from 'src/js/modules/blocks/containers/empty-page-trigger-container';
import ToolsContainer from 'src/js/modules/tools/containers/tools-container';
import HighlighterContainer from 'src/js/modules/highlighter/containers/highlighter-container';
import FloatingTriggerContainer from 'src/js/modules/blocks/containers/floating-trigger-container';
import ControlPaneContainer from 'src/js/modules/control-pane/containers/control-pane-container';
import BlockControlPaneContainer from 'src/js/modules/control-pane/containers/block-control-pane-container';
import DropGuideContainer from 'src/js/modules/drag-n-drop/containers/drop-guide-container';
import TooltipContainer from 'src/js/modules/tooltip/containers/tooltip-container';
import ElementDragLayerContainer from 'src/js/modules/drag-n-drop/containers/element-drag-layer-container';
import ViewLoaderContainer from 'src/js/modules/view-loader/containers/view-loader-container';
import CustomToolsContainer from 'src/js/modules/custom-tools/containers/custom-tools-container';
import TextResizer from 'src/js/modules/text-resizer/containers/text-resizer';
import editor from 'src/js/modules/editor';
import MousePositionContext from 'src/js/modules/common/components/mouse-position-context';
import { UserInputProvider } from 'src/js/modules/common/components/user-input-context';
import getDisabledStatus from 'src/js/modules/view/selectors/disabled-status';
import ViewContainer from 'src/js/modules/view/containers/view-container';
import CustomHtmlControl from 'src/js/modules/extensions/registry/ws-html-widget/components/custom-html-control';
import useContextMenu from 'src/js/modules/context-menu/components/use-context-menu';
import InteractiveToolContainer from 'src/js/modules/interactive-tool/containers/interactive-tool-container';
import useAddWidgetEvent from 'src/js/modules/widgets/hooks/use-add-widget-event';
import getWsBlockInjectedTranslations from 'src/js/modules/blocks/helpers/get-ws-block-injected-translations';
import AnchorHighlighterContainer from 'src/js/modules/highlighter/containers/anchor-highlighter-container';
import useDeviceModeWidth from 'src/js/modules/view/hooks/use-device-mode-width';
import injectTranslationsIntoLiveDoc from '../helpers/inject-translations-into-live-doc';
import injectEditorColors from '../helpers/inject-editor-colors';
import setLiveDocLangAttribute from '../helpers/set-live-doc-lang-attribute';
import initWSEditor from '../helpers/init-ws-editor';

function EditorContainer() {
  const isEditorShown = useSelector(editor.selectors.getVisibilityState);
  const disabled = useSelector(getDisabledStatus);
  const viewportInsets = useSelector(view.selectors.getViewportInsets, shallowEqual);

  const deviceModeWidth = useDeviceModeWidth();

  useAddWidgetEvent(document);
  useContextMenu();

  useEffect(() => {
    const client = serviceClient.get();

    if (client) {
      initWSEditor(client);
    }
  }, []);

  useEffect(() => {
    const { ON_LIVE_DOCUMENT_COMPOSED, ON_LIVE_DOCUMENT_CONNECTED } = hooks.availableHooks;

    const onLiveDocumentComposedHandler = (liveDoc) => {
      injectTranslationsIntoLiveDoc(liveDoc, getWsBlockInjectedTranslations());

      setLiveDocLangAttribute(liveDoc);
    };

    const onLiveDocumentConnectedHandler = (liveDoc) => {
      injectEditorColors(liveDoc);
    };

    hooks.operations.subscribe(ON_LIVE_DOCUMENT_COMPOSED, onLiveDocumentComposedHandler);

    hooks.operations.subscribe(ON_LIVE_DOCUMENT_CONNECTED, onLiveDocumentConnectedHandler);

    return () => {
      hooks.operations.unsubscribe(ON_LIVE_DOCUMENT_COMPOSED, onLiveDocumentComposedHandler);
      hooks.operations.unsubscribe(ON_LIVE_DOCUMENT_CONNECTED, onLiveDocumentConnectedHandler);
    };
  }, []);

  if (!isEditorShown) return null;

  const viewWrapperClass = classNames('ws-view-wrapper', {
    'ws-view-wrapper--disabled': disabled,
  });

  return (
    <UserInputProvider>
      <div className={viewWrapperClass}>
        <ViewContainer />
        <ViewOverlayContainer>
          <TopbarContainer />
          <EmptyPageTriggerContainer />
          <ToolsContainer>
            <MousePositionContext.Provider>
              <MousePositionContext.Consumer>
                {(mousePosition) => (
                  <ViewportConsumer>
                    {({ width, height }) => {
                      // The docked chat panel takes its share off the right edge,
                      // so panes clamp to the narrowed editor, not to the window.
                      const availableWidth = Math.max(
                        0,
                        width - viewportInsets.left - viewportInsets.right
                      );
                      const effectiveWidth = deviceModeWidth || availableWidth;
                      return (
                        <React.Fragment>
                          <HighlighterContainer viewportHeight={height} />
                          <TextResizer />
                          <AnchorHighlighterContainer />
                          <FloatingTriggerContainer
                            viewportHeight={height}
                            mousePosition={mousePosition}
                          />
                          <BlockControlPaneContainer
                            viewportWidth={effectiveWidth}
                            viewportHeight={height}
                          />
                          <ControlPaneContainer
                            viewportWidth={availableWidth}
                            viewportHeight={height}
                            mousePosition={mousePosition}
                          />
                        </React.Fragment>
                      );
                    }}
                  </ViewportConsumer>
                )}
              </MousePositionContext.Consumer>
            </MousePositionContext.Provider>
            <CustomToolsContainer />
            <DropGuideContainer />
            <InteractiveToolContainer />
          </ToolsContainer>
          <ElementDragLayerContainer />
        </ViewOverlayContainer>
        <ViewLoaderContainer />
        <TooltipContainer />
        <CustomHtmlControl />
      </div>
    </UserInputProvider>
  );
}

export default EditorContainer;
